import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Grow,
  Zoom,
  Chip,
  Avatar,
  Stack,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Work,
  People,
  TrendingUp,
  Security,
  Speed,
  Analytics,
  Assessment,
  VideoCall,
  Schedule,
  CheckCircle,
  Star,
  ArrowForward,
  Login,
  PersonAdd,
  AdminPanelSettings,
  BusinessCenter,
  Psychology,
  Engineering,
  Code,
  School,
  TrendingDown,
  TrendingFlat,
  AccessTime,
  LocationOn,
  Email,
  Phone,
  LinkedIn,
  GitHub,
  Language,
  Menu,
  Close,
  VerifiedUser,
  AutoAwesome,
  PsychologyAlt,
  GroupWork,
  Timeline,
  EmojiEvents,
  Lightbulb,
  RocketLaunch,
  Handshake,
  Diversity3,
  WorkspacePremium
} from '@mui/icons-material';

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [animateStats, setAnimateStats] = useState(false);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    // Trigger stats animation after component mounts
    const statsTimer = setTimeout(() => {
      setAnimateStats(true);
    }, 1500);

    return () => {
      clearTimeout(timer);
      clearTimeout(statsTimer);
    };
  }, []);

  const hiringProcess = [
    {
      step: '01',
      title: 'Smart Application',
      description: 'Submit your application through our intelligent platform with tech-driven resume parsing',
      icon: <VerifiedUser />,
      color: '#2196f3'
    },
    {
      step: '02',
      title: 'Technical Assessment',
      description: 'Complete our comprehensive tech-driven assessments to showcase your skills',
      icon: <PsychologyAlt />,
      color: '#4caf50'
    },
    {
      step: '03',
      title: 'Video Interview',
      description: 'Engage in seamless video interviews with our integrated communication tools',
      icon: <VideoCall />,
      color: '#ff9800'
    },
    {
      step: '04',
      title: 'Final Selection',
      description: 'Receive quick feedback and join our dynamic team of professionals',
      icon: <EmojiEvents />,
      color: '#9c27b0'
    }
  ];

  const platformBenefits = [
    {
      title: 'Smart Skill Matching',
      description: 'Our advanced algorithms match your skills with the perfect role opportunities',
      icon: <AutoAwesome />,
      color: '#2196f3'
    },
    {
      title: 'Comprehensive Evaluation',
      description: 'Multi-dimensional assessment covering technical skills, soft skills, and cultural fit',
      icon: <Assessment />,
      color: '#4caf50'
    },
    {
      title: 'Seamless Experience',
      description: 'User-friendly interface with real-time updates and transparent communication',
      icon: <Speed />,
      color: '#ff9800'
    },
    {
      title: 'Career Growth',
      description: 'Continuous learning opportunities and career development support',
      icon: <TrendingUp />,
      color: '#9c27b0'
    }
  ];

  const companyValues = [
    {
      title: 'Innovation',
      description: 'Pushing boundaries with cutting-edge technology and creative solutions',
      icon: <Lightbulb />,
      color: '#ff9800'
    },
    {
      title: 'Excellence',
      description: 'Maintaining the highest standards in everything we do',
      icon: <Star />,
      color: '#ffc107'
    },
    {
      title: 'Collaboration',
      description: 'Fostering teamwork and knowledge sharing across all levels',
      icon: <GroupWork />,
      color: '#4caf50'
    },
    {
      title: 'Integrity',
      description: 'Building trust through transparency and ethical practices',
      icon: <VerifiedUser />,
      color: '#2196f3'
    }
  ];

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'white' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
            RISO Intelligence
          </Typography>
          <LinearProgress sx={{ width: 200, mx: 'auto' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Navigation */}
      <AppBar 
        position="fixed" 
        sx={{ 
          background: 'rgba(255, 255, 255, 0.95)', 
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#1a237e',
              flexGrow: 1 
            }}
          >
            RISO Intelligence
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<AdminPanelSettings />}
            onClick={() => navigate('/admin-login')}
            sx={{ 
              mr: 2,
              borderColor: '#1a237e',
              color: '#1a237e',
              '&:hover': {
                borderColor: '#0d47a1',
                backgroundColor: '#1a237e',
                color: 'white'
              }
            }}
          >
            Admin Login
          </Button>
          
          <Button
            variant="contained"
            startIcon={<Login />}
            onClick={() => navigate('/login')}
            sx={{ 
              background: 'linear-gradient(135deg, #1a237e, #3949ab)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0d47a1, #1a237e)'
              }
            }}
          >
            Candidate Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: '15%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            animation: 'float 8s ease-in-out infinite reverse'
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'white',
                      mb: 2,
                      fontSize: { xs: '2.5rem', md: '3.5rem' }
                    }}
                  >
                    RISO Intelligence
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      mb: 3,
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Hiring Management Portal
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      mb: 4,
                      lineHeight: 1.6
                    }}
                  >
                    Join our innovative team and experience a modern, tech-enabled hiring process 
                    designed to identify and nurture exceptional talent.
                  </Typography>
                  
                  <Slide direction="up" in timeout={1500}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<PersonAdd />}
                        onClick={() => navigate('/register')}
                        sx={{
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          px: 4,
                          py: 1.5,
                          fontSize: '1.1rem',
                          '&:hover': {
                            background: 'rgba(255,255,255,0.3)',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Register as Candidate
                      </Button>
                      
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<Login />}
                        onClick={() => navigate('/login')}
                        sx={{
                          borderColor: 'rgba(255,255,255,0.5)',
                          color: 'white',
                          px: 4,
                          py: 1.5,
                          fontSize: '1.1rem',
                          '&:hover': {
                            borderColor: 'white',
                            background: 'rgba(255,255,255,0.1)',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Login
                      </Button>
                    </Box>
                  </Slide>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Zoom in timeout={2000}>
                <Box
                  sx={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 4,
                    p: 4,
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <Typography variant="h5" sx={{ color: 'white', mb: 3, fontWeight: 'bold' }}>
                    Why Join RISO Intelligence?
                  </Typography>
                  <List sx={{ color: 'white' }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Tech-Driven Hiring Process" 
                        primaryTypographyProps={{ sx: { color: 'white', fontWeight: 'medium' } }}
                        secondary="Experience the future of recruitment with intelligent matching"
                        secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.8)' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Transparent Communication" 
                        primaryTypographyProps={{ sx: { color: 'white', fontWeight: 'medium' } }}
                        secondary="Real-time updates and clear feedback throughout the process"
                        secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.8)' } }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Career Growth Opportunities" 
                        primaryTypographyProps={{ sx: { color: 'white', fontWeight: 'medium' } }}
                        secondary="Continuous learning and professional development support"
                        secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.8)' } }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Hiring Process Section */}
      <Box sx={{ py: 8, background: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                color: '#1a237e',
                mb: 2
              }}
            >
              Our Hiring Process
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Experience a streamlined, technology-driven hiring process designed to 
              showcase your skills and potential effectively.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {hiringProcess.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={1000 + index * 200}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -15,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: step.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        zIndex: 10,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                      }}
                    >
                      {step.step}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `${step.color}15`,
                        mb: 3,
                        mx: 'auto',
                        mt: 2
                      }}
                    >
                      <Box sx={{ color: step.color, fontSize: 40 }}>
                        {step.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        color: '#1a237e'
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {step.description}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Platform Benefits Section */}
      <Box sx={{ py: 8, background: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                color: '#1a237e',
                mb: 2
              }}
            >
              Platform Benefits
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Discover how our advanced platform enhances your hiring experience 
              and career opportunities.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {platformBenefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={1000 + index * 200}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `${benefit.color}15`,
                        mb: 3,
                        mx: 'auto'
                      }}
                    >
                      <Box sx={{ color: benefit.color, fontSize: 40 }}>
                        {benefit.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        color: '#1a237e'
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {benefit.description}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Company Values Section */}
      <Box sx={{ py: 8, background: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                color: '#1a237e',
                mb: 2
              }}
            >
              Our Values
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              We are guided by core values that shape our culture and drive our success.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {companyValues.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={1000 + index * 200}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `${value.color}15`,
                        mb: 3,
                        mx: 'auto'
                      }}
                    >
                      <Box sx={{ color: value.color, fontSize: 40 }}>
                        {value.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        color: '#1a237e'
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {value.description}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1a237e, #3949ab)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Ready to Join Our Team?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Start your journey with RISO Intelligence and be part of an innovative 
              team that values talent and growth.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<PersonAdd />}
                onClick={() => navigate('/register')}
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.3)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Apply Now
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                startIcon={<Login />}
                onClick={() => navigate('/login')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, background: '#1a237e', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                RISO Intelligence
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Revolutionizing the hiring process with AI-powered intelligence and 
                comprehensive assessment tools.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Quick Links
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  color="inherit"
                  onClick={() => navigate('/login')}
                  sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                >
                  Candidate Login
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigate('/admin-login')}
                  sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                >
                  Admin Login
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigate('/register')}
                  sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                >
                  Register
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.2)' }} />
          <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.6 }}>
            © 2024 RISO Intelligence. All rights reserved.
          </Typography>
        </Container>
      </Box>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}
      </style>
    </Box>
  );
};

export default HomePage;
